import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import { EmptyState, FormField, StateMessage } from "@/ds";
import { DetailField, DetailSectionFields, ProfissionalSelect } from "@/shared/components";
import { FichaAnexos } from "../ficha-anexos";
import { FichaAnexosPrevia } from "../ficha-anexos-previa";
import { FichaCustosTable } from "../ficha-custos-table";
import {
  ANEXOS_ADICIONAR,
  ANEXOS_SALVAR_ANTES,
  ANEXOS_VAZIO_TITULO,
  EMAIL_LABEL,
  EXPORTAR_LABEL,
  ORCAMENTO_EM_BREVE_DESCRICAO,
  ORCAMENTO_EM_BREVE_TITULO,
  SEM_VALOR,
} from "./ficha-secao.constants";
import styles from "./ficha-secao.module.scss";
import type { FichaField, FichaFieldEdit, FichaSecaoViewModel } from "./types-ficha-secao";

function CampoEditavel({ label, edit }: { label: string; edit: FichaFieldEdit }) {
  return (
    <FormField.Root className={styles.campoEdicao} error={edit.error} testId={edit.testId}>
      <FormField.Label className={styles.rotulo}>{label}</FormField.Label>
      <div className={styles.edicao}>
        <FormField.Control>
          {edit.kind === "usuario" ? (
            <ProfissionalSelect
              className={styles.controle}
              value={edit.value || null}
              options={edit.opcoes ?? []}
              errorMessage={edit.opcoesErro}
              loading={edit.carregando}
              invalid={Boolean(edit.error)}
              onChange={(valor) => edit.onChange(valor ?? "")}
              ariaLabel={label}
              testId={edit.inputTestId}
            />
          ) : (
            <InputText
              className={styles.controle}
              value={edit.value}
              onChange={(event) => edit.onChange(event.target.value)}
              aria-label={label}
              data-testid={edit.inputTestId}
            />
          )}
        </FormField.Control>
        <FormField.Error />
      </div>
    </FormField.Root>
  );
}

function Campos({ fields }: { fields: FichaField[] }) {
  if (fields.length === 0) return <p className={styles.valor}>{SEM_VALOR}</p>;

  return (
    <DetailSectionFields variant="linha">
      {fields.map((field) =>
        field.edit ? (
          <CampoEditavel key={field.label} label={field.label} edit={field.edit} />
        ) : (
          <DetailField
            key={field.label}
            label={field.label}
            value={field.value}
            hint={field.hint}
            variant="linha"
          />
        ),
      )}
    </DetailSectionFields>
  );
}

function AnexosAguardandoFicha() {
  return (
    <EmptyState.Root size="sm" testId="ficha-anexos-empty" className={styles.anexosVazio}>
      <EmptyState.Illustration name="sem-equipe" />
      <EmptyState.Title>{ANEXOS_VAZIO_TITULO}</EmptyState.Title>
      <EmptyState.Description>{ANEXOS_SALVAR_ANTES}</EmptyState.Description>
    </EmptyState.Root>
  );
}

/** Título da seção, ações do topo e corpo conforme o tipo (campos, custos, orçamento ou anexos). */
export function FichaSecaoView({
  section,
  tituloId,
  anexosDaFicha,
  anexosDaPrevia,
  anexosAguardandoFicha,
  acoesTopo,
  orcamentoEmBreve,
  custos,
  campos,
}: FichaSecaoViewModel) {
  if (anexosDaFicha) {
    return (
      <FichaAnexos
        obraId={anexosDaFicha.obraId}
        fichaId={anexosDaFicha.fichaId}
        title={section.title}
      />
    );
  }

  if (anexosDaPrevia) {
    return <FichaAnexosPrevia title={section.title} {...anexosDaPrevia} />;
  }

  return (
    <section
      className={styles.secao}
      data-section-id={section.id}
      aria-labelledby={tituloId}
      data-testid={section.testId}
    >
      <div className={styles.cabecalho}>
        <h3 className={styles.titulo} id={tituloId}>
          {section.title}
        </h3>
        {acoesTopo ? (
          <div className={styles.acoes}>
            <Button
              type="button"
              label={EMAIL_LABEL}
              severity="secondary"
              outlined
              disabled={acoesTopo.exportando}
              onClick={acoesTopo.enviarEmail}
              data-testid="ficha-email"
            />
            <Button
              type="button"
              label={EXPORTAR_LABEL}
              severity="secondary"
              loading={acoesTopo.exportando}
              disabled={acoesTopo.exportando}
              onClick={acoesTopo.exportar}
              data-testid="ficha-export"
            />
          </div>
        ) : null}
        {anexosAguardandoFicha ? (
          <div className={styles.acoes}>
            <Button
              type="button"
              label={ANEXOS_ADICIONAR}
              icon="pi pi-upload"
              severity="secondary"
              disabled
              title={ANEXOS_SALVAR_ANTES}
              data-testid="ficha-anexos-add"
            />
          </div>
        ) : null}
      </div>

      {orcamentoEmBreve ? (
        <StateMessage
          variant="info"
          title={ORCAMENTO_EM_BREVE_TITULO}
          description={ORCAMENTO_EM_BREVE_DESCRICAO}
          testId="ficha-orcamento-em-breve"
        />
      ) : null}
      {anexosAguardandoFicha ? <AnexosAguardandoFicha /> : null}
      {custos ? (
        <>
          <Campos fields={section.fields} />
          <FichaCustosTable tiposObra={section.custosTipos ?? []} />
        </>
      ) : null}
      {campos ? <Campos fields={section.fields} /> : null}
    </section>
  );
}
